import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EDITABLE_DIAMETERS_MM,
  EDITABLE_GRADES,
  EDITABLE_LENGTHS_M,
  PRICE_LEVEL_OPTIONS,
  PriceLevel,
  type QuotationLineItemDto,
} from '../types/quotation.types';

const UNCHANGED = '__unchanged__';

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onApply: (patch: Partial<QuotationLineItemDto>) => void;
}

interface FormState {
  diameter: string;
  length: string;
  grade: string;
  weldable: string;
  quantity: string;
  priceLevel: string;
}

const EMPTY: FormState = {
  diameter: UNCHANGED,
  length: UNCHANGED,
  grade: UNCHANGED,
  weldable: UNCHANGED,
  quantity: '',
  priceLevel: UNCHANGED,
};

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring';

export function BulkEditDialog({ open, onOpenChange, selectedCount, onApply }: BulkEditDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const patch = buildPatch(form);
  const hasChanges = Object.keys(patch).length > 0;

  const handleApply = () => {
    if (!hasChanges) return;
    onApply(patch);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk edit {selectedCount} line item{selectedCount === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Only the fields you change are applied. Leave a field as
            <span className="mx-1 font-medium">— unchanged —</span>
            to skip it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <Field label="Diameter">
            <BulkSelect value={form.diameter} onValueChange={(v) => setForm((f) => ({ ...f, diameter: v }))}>
              {EDITABLE_DIAMETERS_MM.map((d) => (
                <SelectItem key={d} value={String(d)}>{d} mm</SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Length">
            <BulkSelect value={form.length} onValueChange={(v) => setForm((f) => ({ ...f, length: v }))}>
              {EDITABLE_LENGTHS_M.map((l) => (
                <SelectItem key={l} value={String(l)}>{l} m</SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Grade">
            <BulkSelect value={form.grade} onValueChange={(v) => setForm((f) => ({ ...f, grade: v }))}>
              {EDITABLE_GRADES.map((g) => (
                <SelectItem key={g} value={g}>G{g}</SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Weldable">
            <BulkSelect value={form.weldable} onValueChange={(v) => setForm((f) => ({ ...f, weldable: v }))}>
              <SelectItem value="Y">Y</SelectItem>
              <SelectItem value="N">N</SelectItem>
            </BulkSelect>
          </Field>

          <Field label="Qty">
            <input
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              className={inputCls}
              value={form.quantity}
              placeholder="— unchanged —"
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          </Field>

          <Field label="Price Level">
            <BulkSelect value={form.priceLevel} onValueChange={(v) => setForm((f) => ({ ...f, priceLevel: v }))}>
              {PRICE_LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </BulkSelect>
          </Field>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={!hasChanges}>
            Apply to {selectedCount} row{selectedCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Themed Select for the bulk-edit fields (86d3c38ve). The "— unchanged —" row is
// the always-present first option; since `value` is never empty (defaults to
// UNCHANGED) the trigger renders the selected item's own text — no placeholder.
function BulkSelect({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNCHANGED}>— unchanged —</SelectItem>
        {children}
      </SelectContent>
    </Select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[100px_1fr] items-center gap-3">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div>{children}</div>
    </div>
  );
}

function buildPatch(form: FormState): Partial<QuotationLineItemDto> {
  const patch: Partial<QuotationLineItemDto> = {};
  if (form.diameter !== UNCHANGED) patch.diameterMm = Number(form.diameter);
  if (form.length !== UNCHANGED) patch.lengthM = Number(form.length);
  if (form.grade !== UNCHANGED) patch.gradeCode = form.grade;
  if (form.weldable !== UNCHANGED) patch.isWeldable = form.weldable === 'Y';
  if (form.quantity.trim() !== '') {
    const q = Number(form.quantity);
    if (!Number.isNaN(q)) patch.quantity = q;
  }
  if (form.priceLevel !== UNCHANGED) {
    patch.priceLevel = form.priceLevel as PriceLevel;
  }
  return patch;
}
